// src/App.jsx
// STATUS: active | UPDATED: 2026-09-27
// LOG:
//   2026-09-27  /dev/marks/, a lazy route that exists only in local dev.
//   2026-09-19  /admin/manager-review/ routed.
//   2026-09-17  /manager/ and /manager/steps/ routed, minRole manager.
//   2026-09-17  /notes/ and /notes/:slug/ routed.
//   2026-09-15  /admin/questions/ routed.
//   2026-08-02  Progress hydration wired: after auth resolves, remote training
//               progress is pulled and merged into the local cache before the
//               app shell renders, so every page reads merged state. Bounded
//               by a 3s timeout inside hydrateProgress; a slow or absent
//               network never blocks startup. Header standardized (e765830).
//   2026-08-01  Nav trio routes: /coach/ added; roadmap routes /courses/roadmap/ and :slug/
// NEXT: Admin route + /team/ manager view land in Phase 2
// Top-level routes. Public: /login/, /signup/, /reset-password/.
// Protected: /dashboard/, /courses/ (Training hub), /courses/roadmap/ and
// /courses/roadmap/:slug/ (the 13-step path), /coach/, /calendar/, /team/,
// /settings/.

import { lazy, Suspense, useEffect, useState } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Box, Spinner, Center } from '@chakra-ui/react'
import { useAuth } from './hooks/useAuth'
import { hydrateProgress } from './lib/progressStore'
import ProtectedRoute from './components/auth/ProtectedRoute'
import AppShell from './components/layout/AppShell'
import Login from './pages/Login'
import Signup from './pages/Signup'
import ResetPassword from './pages/ResetPassword'
import Dashboard from './pages/Dashboard'
import CalendarPage from './pages/Calendar'
import Team from './pages/Team'
import Person from './pages/Team/Person.jsx'
import Courses from './pages/Courses'
import Roadmap from './pages/Roadmap'
import StepDetail from './pages/Roadmap/StepDetail'
import Reference from './pages/Roadmap/Reference'
import Coach from './pages/Coach'
import Admin from './pages/Admin'
import AdminDealerships from './pages/AdminDealerships'
import StorefrontReport from './pages/StorefrontReport'
import AdminDigital from './pages/AdminDigital'
import AdminTeam from './pages/AdminTeam'
import AdminLeads from './pages/AdminLeads'
import AdminMethod from './pages/AdminMethod'
import AdminManagerReview from './pages/AdminManagerReview'
import AdminQuestions from './pages/AdminQuestions'
import Manager from './pages/Manager'
import ManagerSystem from './pages/Manager/System'
import ManagerSteps from './pages/Manager/Steps'
import OneOnOne from './pages/Manager/OneOnOne'
import MorningMeeting from './pages/Manager/MorningMeeting'
import ManagerWeek from './pages/Manager/Week'
import Notes from './pages/Notes'
import Numbers from './pages/Numbers'
import NoteView from './pages/Notes/NoteView'
import AdminOnboard from './pages/AdminOnboard'
import Settings from './pages/Settings'

// Local development only. The ternary is resolved at build time, so production
// carries neither the route nor the dynamic import. See the page's header.
const MarksPreview = import.meta.env.DEV ? lazy(() => import('./pages/dev/MarksPreview.jsx')) : null

export default function App() {
  const { loading, user } = useAuth()
  const [hydrated, setHydrated] = useState(false)

  useEffect(() => {
    let alive = true
    if (!user?.id) {
      setHydrated(true)
      return undefined
    }
    setHydrated(false)
    hydrateProgress(user.id).finally(() => {
      if (alive) setHydrated(true)
    })
    return () => {
      alive = false
    }
  }, [user?.id])

  if (loading || !hydrated) {
    return (
      <Center minH="100vh" bg="bg">
        <Spinner size="lg" color="ink" thickness="3px" />
      </Center>
    )
  }

  return (
    <Box minH="100vh" bg="bg">
      <Routes>
        <Route path="/login/" element={<Login />} />
        <Route path="/signup/" element={<Signup />} />
        <Route path="/reset-password/" element={<ResetPassword />} />
        {/* Janda Digital. The only public page in the app: a dealer principal
            reading this has no account. Token resolved server side. */}
        <Route path="/report/:token/" element={<StorefrontReport />} />
        <Route
          element={
            <ProtectedRoute>
              <AppShell />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<Navigate to="/dashboard/" replace />} />
          <Route path="/dashboard/" element={<Dashboard />} />
          <Route path="/courses/" element={<Courses />} />
          <Route path="/courses/roadmap/" element={<Roadmap />} />
          <Route path="/courses/roadmap/:slug/" element={<StepDetail />} />
          <Route path="/courses/reference/" element={<Reference />} />
          <Route path="/coach/" element={<Coach />} />
          {MarksPreview && (
            <Route
              path="/dev/marks/"
              element={
                <Suspense fallback={null}>
                  <MarksPreview />
                </Suspense>
              }
            />
          )}
          <Route
            path="/admin/"
            element={
              <ProtectedRoute requireAdmin>
                <Admin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/dealerships/"
            element={
              <ProtectedRoute requireAdmin>
                <AdminDealerships />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/digital/"
            element={
              <ProtectedRoute requireAdmin>
                <AdminDigital />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/leads/"
            element={
              <ProtectedRoute requireAdmin>
                <AdminLeads />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/team/"
            element={
              <ProtectedRoute requireAdmin>
                <AdminTeam />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manager/"
            element={
              <ProtectedRoute minRole="manager">
                <Manager />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manager/morning/"
            element={
              <ProtectedRoute minRole="manager">
                <MorningMeeting />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manager/one-on-one/"
            element={
              <ProtectedRoute minRole="manager">
                <OneOnOne />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manager/week/"
            element={
              <ProtectedRoute minRole="manager">
                <ManagerWeek />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manager/system/"
            element={
              <ProtectedRoute minRole="manager">
                <ManagerSystem />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manager/steps/"
            element={
              <ProtectedRoute minRole="manager">
                <ManagerSteps />
              </ProtectedRoute>
            }
          />
          <Route
            path="/notes/"
            element={
              <ProtectedRoute requireAdmin>
                <Notes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/notes/:slug/"
            element={
              <ProtectedRoute requireAdmin>
                <NoteView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/manager-review/"
            element={
              <ProtectedRoute requireAdmin>
                <AdminManagerReview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/questions/"
            element={
              <ProtectedRoute requireAdmin>
                <AdminQuestions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/method/"
            element={
              <ProtectedRoute requireAdmin>
                <AdminMethod />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/onboard/"
            element={
              <ProtectedRoute requireAdmin>
                <AdminOnboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/numbers/"
            element={
              <ProtectedRoute minRole="owner">
                <Numbers />
              </ProtectedRoute>
            }
          />
          <Route path="/calendar/" element={<CalendarPage />} />
          {/* A salesperson has no floor. RLS already returns only themselves,
              so this is about not offering a page that would be empty. */}
          <Route
            path="/team/"
            element={
              <ProtectedRoute minRole="manager">
                <Team />
              </ProtectedRoute>
            }
          />
          {/* One person, from above. Same minRole as the board it hangs off,
              and the page itself checks nothing: RLS decides whether the row
              comes back, and a person outside your store reads as not found
              rather than as an error. */}
          <Route
            path="/team/:personId/"
            element={
              <ProtectedRoute minRole="manager">
                <Person />
              </ProtectedRoute>
            }
          />
          <Route path="/settings/" element={<Settings />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Box>
  )
}
